import { Header } from '../../components/common';
import CustomSingleDatePicker from '../../components/datePicker/CustomSingleDatePicker';
import Paper from '@mui/material/Paper';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, CircularProgress, Collapse, Tab, Tabs, Typography } from '@mui/material';
import {
  exportSentEmailStudentsDocx,
  getSentEmailsRfLocationEntryDate25,
  getSentEmailsVisaInvitation55,
  getRectorApprovalSentEmails,
  getStudentExpelledSentEmails,
  getManualSentEmails,
  sendRectorApproval,
  exportRectorApprovalDocx,
  parseVisaPdfs,
  updateStudentsFromVisa,
  sendVisaEmails,
  getVisaParsedSentEmails,
} from '../../actions/mail';
import VisaEmailModal from '../../components/Modals/VisaEmailModal';
import iziToast from 'izitoast';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import './emailsPage.css';
import moment from 'moment';
import ModalCustom from '../../components/ModalCustom/ModalCustom';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';
import { studentCardPath } from '../../utils/consts/educationType';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDateForApi = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const offset = d.getTimezoneOffset();
  return new Date(d.getTime() - offset * 60000).toISOString().split('T')[0];
};

const resolveRecipient = (student) => {
  if (student.agent_email) return student.agent_email;
  if (student.representative_email) return student.representative_email;
  return student.first_student_email || student.second_student_email || '—';
};

const showError = (err) => {
  iziToast.error({
    message: err?.response?.data?.message ?? err?.response?.data?.error ?? 'Ошибка получения данных',
    position: 'topRight',
    color: '#FFF2ED',
  });
};

// ─── Shared UI ────────────────────────────────────────────────────────────────

const EmailsTableCell = ({ value, type = 'row' }) => (
  <TableCell
    sx={{
      whiteSpace: 'nowrap',
      fontSize: '14px',
      fontFamily: 'Montserrat',
      fontWeight: type === 'header' ? '550' : '400',
    }}
  >
    {value}
  </TableCell>
);

const EmailLink = ({ emailId }) => (
  <a
    href={`https://web32.beget.email/?_task=mail&_action=show&_uid=${emailId}&_mbox=INBOX`}
    target="_blank"
    rel="noopener noreferrer"
    className="linkToCard"
  >
    Открыть
  </a>
);

const NoDataRow = ({ colSpan = 5 }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center">
      <Box sx={{ py: 2, fontFamily: 'Montserrat', color: '#9E9E9E', fontSize: '14px' }}>
        Нет данных
      </Box>
    </TableCell>
  </TableRow>
);

const FilterRow = ({ children, isLoading }) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'center', mb: 3 }}>
    {children}
    {isLoading && <CircularProgress color="warning" size={24} />}
  </Box>
);

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

// ─── RECTOR APPROVAL TAB ──────────────────────────────────────────────────────

const RecipientTooltipContent = ({ student }) => {
  const items = [
    { label: 'Почта агента', value: student.agent_email },
    { label: 'Почта представителя', value: student.representative_email },
    { label: 'Почта студента (1)', value: student.first_student_email },
    { label: 'Почта студента (2)', value: student.second_student_email },
  ];
  return (
    <Box sx={{ fontFamily: 'Montserrat', fontSize: '12px', lineHeight: 1.8 }}>
      {items.map(({ label, value }) => (
        <div key={label}><b>{label}:</b> {value || '—'}</div>
      ))}
    </Box>
  );
};

const RectorWeekGroup = ({ group, isSent, isOpen, onToggle, onSendSuccess }) => {
  const { can } = useCurrentUser();
  const [modalOpen, setModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = (e) => {
    e.stopPropagation();
    setIsExporting(true);
    exportRectorApprovalDocx(group.week_start, group.week_end)
      .catch((err) => {
        iziToast.error({ message: internalServerError(err), position: 'topRight', color: '#FFF2ED' });
      })
      .finally(() => setIsExporting(false));
  };

  const handleSend = () => {
    const studentIds = group.students.map((s) => s.id);
    setIsSending(true);
    sendRectorApproval(studentIds)
      .then((res) => {
        const { sent, total, failed } = res.data;
        const msg = `Отправлено ${sent} из ${total} писем`;
        if (failed.length > 0) {
          iziToast.warning({ message: `${msg}. Не отправлено: ${failed.length}`, position: 'topRight' });
        } else {
          iziToast.success({ message: msg, position: 'topRight' });
        }
        onSendSuccess?.();
      })
      .catch((e) => {
        iziToast.error({ message: internalServerError(e), position: 'topRight', color: '#FFF2ED' });
      })
      .finally(() => {
        setIsSending(false);
        setModalOpen(false);
      });
  };
  // Раньше здесь стояла проверка на конкретные id (1 и 6). Право теперь выдаётся строкой
  // в user_permissions, и сервер проверяет его же — сместился только источник истины.
  const canSendRectorApproval = can(PERMISSIONS.MAIL_RECTOR_APPROVAL_SEND);

  return (
    <Fragment>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ cursor: 'pointer', backgroundColor: '#F5F5F5', '&:hover': { backgroundColor: '#EEEEEE' } }}
      >
        <TableCell width={40} sx={{ p: '4px 8px' }}>
          <IconButton size="small" onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}>
            {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell colSpan={isSent ? 5 : 4} sx={{ fontFamily: 'Montserrat', fontWeight: '550', fontSize: '13px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
            <span>
              {moment(group.week_start).format('DD.MM.YYYY')} — {moment(group.week_end).format('DD.MM.YYYY')}
            </span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ color: '#666', fontWeight: '400' }}>{group.students.length} студ.</span>
              {!isSent && canSendRectorApproval && (
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(true);
                  }}
                  sx={{ fontFamily: 'Montserrat', textTransform: 'none', fontSize: '12px' }}
                >
                  Отправить рассылку
                </Button>
              )}
              {isSent && (
                <Tooltip title="Выгрузить docx">
                  <span>
                    <Button
                      variant="text"
                      size="small"
                      color="success"
                      onClick={handleExport}
                      disabled={isExporting}
                      sx={{ minWidth: 0, p: '4px' }}
                    >
                      {isExporting
                        ? <CircularProgress size={16} color="inherit" />
                        : <FileDownloadOutlinedIcon fontSize="small" />
                      }
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={isSent ? 6 : 5} sx={{ p: 0 }}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                  <EmailsTableCell value="ФИО (лат.)" type="header" />
                  <EmailsTableCell value="ФИО (кир.)" type="header" />
                  <EmailsTableCell value="Страна" type="header" />
                  <EmailsTableCell value="Дата рождения" type="header" />
                  <EmailsTableCell value="Получатель" type="header" />
                  {isSent && <EmailsTableCell value="Дата отправки" type="header" />}
                  {isSent && <EmailsTableCell value="Ссылка на письмо" type="header" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {group.students.map((student) => (
                  <TableRow key={student.id}>
                    <EmailsTableCell value={
                      <Link
                        to={studentCardPath(student.education_type, student.id)}
                        className="linkToCard"
                      >
                        {student.latin_name || '—'}
                      </Link>
                    } />
                    <EmailsTableCell value={
                      <Link
                        to={studentCardPath(student.education_type, student.id)}
                        className="linkToCard"
                      >
                        {student.russian_name || '—'}
                      </Link>
                    } />
                    <EmailsTableCell value={student.country || '—'} />
                    <EmailsTableCell
                      value={student.birth_date ? moment(student.birth_date).format('DD.MM.YYYY') : '—'}
                    />
                    <EmailsTableCell
                      value={
                        <Tooltip title={<RecipientTooltipContent student={student} />} arrow placement="top">
                          <span style={{ cursor: 'help', borderBottom: '1px dotted #888' }}>
                            {resolveRecipient(student)}
                          </span>
                        </Tooltip>
                      }
                    />
                    {isSent && (
                      <EmailsTableCell
                        value={
                          student.rector_approval_sent_at
                            ? moment(student.rector_approval_sent_at).format('DD.MM.YYYY HH:mm')
                            : '—'
                        }
                      />
                    )}
                    {isSent && (
                      <EmailsTableCell
                        value={student.rector_approval_email_id
                          ? <EmailLink emailId={student.rector_approval_email_id} />
                          : '—'
                        }
                      />
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>

      <ModalCustom active={modalOpen} setActive={setModalOpen}>
        <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', mb: 1 }}>
          Отправить рассылку согласования ректора
        </Typography>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#555', mb: 0.5 }}>
          Период: {moment(group.week_start).format('DD.MM.YYYY')} — {moment(group.week_end).format('DD.MM.YYYY')}
        </Typography>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#555', mb: 2 }}>
          Количество студентов: {group.students.length}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setModalOpen(false)}
            disabled={isSending}
            sx={{ fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSend}
            disabled={isSending}
            sx={{ fontFamily: 'Montserrat', textTransform: 'none' }}
          >
            {isSending ? <CircularProgress size={16} color="inherit" /> : 'Отправить'}
          </Button>
        </Box>
      </ModalCustom>
    </Fragment>
  );
};

const RectorSection = ({ title, weekGroups, isSent, bgColor, textColor, openWeeks, onToggle, onSendSuccess }) => {
  const total = weekGroups.reduce((sum, g) => sum + g.students.length, 0);
  const sectionKey = isSent ? 'sent' : 'not_sent';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontFamily: 'Montserrat',
          fontWeight: 600,
          fontSize: '15px',
          color: textColor,
          backgroundColor: bgColor,
          px: 2,
          py: 1,
          borderRadius: '4px 4px 0 0',
          borderLeft: `3px solid ${textColor}`,
        }}
      >
        {title} ({total})
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: '0 0 4px 4px', boxShadow: 1 }}>
        <Table>
          <TableBody>
            {weekGroups.length === 0 ? (
              <NoDataRow colSpan={isSent ? 6 : 5} />
            ) : (
              weekGroups.map((group) => {
                const weekKey = `${sectionKey}_${group.week_start}`;
                return (
                  <RectorWeekGroup
                    key={weekKey}
                    group={group}
                    isSent={isSent}
                    isOpen={!!openWeeks[weekKey]}
                    onToggle={() => onToggle(weekKey)}
                    onSendSuccess={onSendSuccess}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RectorApprovalTab = () => {
  const [fromDate, setFromDate] = useState(() => moment().startOf('isoWeek').toDate());
  const [toDate, setToDate] = useState(() => moment().toDate());
  const [data, setData] = useState({ sent: [], not_sent: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [openWeeks, setOpenWeeks] = useState({});

  const fetchData = useCallback(() => {
    const from = formatDateForApi(fromDate);
    const to = formatDateForApi(toDate);
    if (!from || !to) return;

    setIsLoading(true);
    getRectorApprovalSentEmails(from, to)
      .then((res) => setData(res.data))
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleWeek = (key) => setOpenWeeks((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <FilterRow isLoading={isLoading}>
        <CustomSingleDatePicker
          name="from"
          label="С"
          size="default"
          defaultValue={fromDate}
          action={setFromDate}
        />
        <CustomSingleDatePicker
          name="to"
          label="По"
          size="default"
          defaultValue={toDate}
          action={setToDate}
        />
      </FilterRow>

      <RectorSection
        title="Не отправлено"
        weekGroups={data.not_sent || []}
        isSent={false}
        bgColor="#FFF2ED"
        textColor="#FA7A45"
        openWeeks={openWeeks}
        onToggle={toggleWeek}
        onSendSuccess={fetchData}
      />
      <RectorSection
        title="Отправлено"
        weekGroups={data.sent || []}
        isSent={true}
        bgColor="#E8F5E9"
        textColor="#2E7D32"
        openWeeks={openWeeks}
        onToggle={toggleWeek}
        onSendSuccess={fetchData}
      />
    </>
  );
};

// ─── VISA INVITATION 55 TAB ───────────────────────────────────────────────────

const VisaInvitation55Tab = () => {
  const [filterDate, setFilterDate] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getSentEmailsVisaInvitation55(filterDate)
      .then((res) => setRows(res.data))
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [filterDate]);

  return (
    <>
      <FilterRow isLoading={isLoading}>
        <CustomSingleDatePicker
          name="sent_at"
          label="Фильтр по дате рассылки"
          size="default"
          action={setFilterDate}
        />
      </FilterRow>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <EmailsTableCell value="Дата отправки" type="header" />
              <EmailsTableCell value="ФИО (лат.)" type="header" />
              <EmailsTableCell value="ФИО (кир.)" type="header" />
              <EmailsTableCell value="Пользователь" type="header" />
              <EmailsTableCell value="Ссылка на письмо" type="header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, idx) => (
                <TableRow key={row.email_id ?? idx}>
                  <EmailsTableCell value={moment(row.sent_at).format('DD.MM.YYYY HH:mm')} />
                  <EmailsTableCell value={
                    <Link
                      to={studentCardPath(row.education_type, row.id)}
                      className="linkToCard"
                    >
                      {row.latin_name || '—'}
                    </Link>
                  } />
                  <EmailsTableCell value={
                    <Link
                      to={studentCardPath(row.education_type, row.id)}
                      className="linkToCard"
                    >
                      {row.russian_name || '—'}
                    </Link>
                  } />
                  <EmailsTableCell value={row.user_name || '—'} />
                  <EmailsTableCell value={<EmailLink emailId={row.email_id} />} />
                </TableRow>
              ))
            ) : (
              <NoDataRow colSpan={5} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ─── RF LOCATION ENTRY DATE 25 TAB ────────────────────────────────────────────

const RfLocation25Tab = () => {
  const [emails, setEmails] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [openRows, setOpenRows] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getSentEmailsRfLocationEntryDate25(filterDate)
      .then((res) => setEmails(res.data))
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [filterDate]);

  const toggleRow = (id) => setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleExport = (exportFileUrl, sentAt) => {
    setIsLoading(true);
    exportSentEmailStudentsDocx(exportFileUrl, sentAt)
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <FilterRow isLoading={isLoading}>
        <CustomSingleDatePicker
          name="sent_at"
          label="Фильтр по дате рассылки"
          size="default"
          action={setFilterDate}
        />
      </FilterRow>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <EmailsTableCell value="Дата отправки" type="header" />
              <EmailsTableCell value="Количество студентов" type="header" />
              <EmailsTableCell value="Ссылка на письмо" type="header" />
              <EmailsTableCell value="Экспорт" type="header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.length > 0 ? (
              emails.map((row) => (
                <Fragment key={row.email_id}>
                  <TableRow>
                    <TableCell>
                      <IconButton onClick={() => toggleRow(row.email_id)}>
                        {openRows[row.email_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <EmailsTableCell value={moment(row.sent_at).format('DD.MM.YYYY HH:mm')} />
                    <EmailsTableCell value={row.students.length} />
                    <EmailsTableCell value={<EmailLink emailId={row.email_id} />} />
                    <EmailsTableCell
                      value={
                        <Button variant="text" color="warning" onClick={() => {
                          handleExport(row.export_file_url, moment(row.sent_at).format('DD.MM.YYYY'));
                        }}>
                          <Tooltip title="Выгрузить docx студентов">
                            <FileDownloadOutlinedIcon />
                          </Tooltip>
                        </Button>
                      }
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0 }}>
                      <Collapse in={!!openRows[row.email_id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <EmailsTableCell value="ФИО (лат.)" />
                                <EmailsTableCell value="ФИО (кир.)" />
                                <EmailsTableCell value="Тип обучения" />
                                <EmailsTableCell value="Кол-во часов" />
                                <EmailsTableCell value="Страна" />
                                <EmailsTableCell value="Пол" />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.students.map((student) => (
                                <TableRow key={student.id}>
                                  <EmailsTableCell
                                    value={
                                      <Link
                                        to={studentCardPath(student.education_type, student.id)}
                                        className="linkToCard"
                                      >
                                        {student.latin_name || '—'}
                                      </Link>
                                    }
                                  />
                                  <EmailsTableCell
                                    value={
                                      <Link
                                        to={studentCardPath(student.education_type, student.id)}
                                        className="linkToCard"
                                      >
                                        {student.russian_name || '—'}
                                      </Link>
                                    }
                                  />
                                  <EmailsTableCell value={student.education_type} />
                                  <EmailsTableCell value={student.hours_number} />
                                  <EmailsTableCell value={student.country} />
                                  <EmailsTableCell value={student.gender} />
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))
            ) : (
              <NoDataRow colSpan={5} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ─── SIMPLE FLAT EMAILS TAB (Отчисленные / Ручная рассылка) ──────────────────

const SimpleEmailsTab = ({ fetchFn }) => {
  const [filterDate, setFilterDate] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetchFn(filterDate)
      .then((res) => setRows(res.data))
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [filterDate, fetchFn]);

  return (
    <>
      <FilterRow isLoading={isLoading}>
        <CustomSingleDatePicker
          name="sent_at"
          label="Фильтр по дате рассылки"
          size="default"
          action={setFilterDate}
        />
      </FilterRow>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <EmailsTableCell value="Дата отправки" type="header" />
              <EmailsTableCell value="ФИО (лат.)" type="header" />
              <EmailsTableCell value="ФИО (кир.)" type="header" />
              <EmailsTableCell value="Ссылка на письмо" type="header" />
              <EmailsTableCell value="Пользователь" type="header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, idx) => (
                <TableRow key={row.email_id ?? idx}>
                  <EmailsTableCell value={moment(row.sent_at).format('DD.MM.YYYY HH:mm')} />
                  <EmailsTableCell value={
                    <Link
                      to={studentCardPath(row.education_type, row.id)}
                      className="linkToCard"
                    >
                      {row.latin_name || '—'}
                    </Link>
                  } />
                  <EmailsTableCell value={
                    <Link
                      to={studentCardPath(row.education_type, row.id)}
                      className="linkToCard"
                    >
                      {row.russian_name || '—'}
                    </Link>
                  } />
                  <EmailsTableCell value={<EmailLink emailId={row.email_id} />} />
                  <EmailsTableCell value={row.user_name || '—'} />
                </TableRow>
              ))
            ) : (
              <NoDataRow colSpan={5} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ─── VISA PARSING TAB ────────────────────────────────────────────────────────

const pluralDocs = (n) => {
  if (n % 10 === 1 && n % 100 !== 11) return 'документ';
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return 'документа';
  return 'документов';
};

const ResultStatusCell = ({ result }) => {
  if (result.student) {
    return (
      <TableCell sx={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#2E7D32' }}>
        ✓ Найден
      </TableCell>
    );
  }
  return (
    <TableCell sx={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#C62828' }}>
      ✗ Не найден
    </TableCell>
  );
};

const VisaParsingTab = () => {
  const fileInputRef = useRef(null);

  // Парсинг
  const [isParsing, setIsParsing] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [parsedResults, setParsedResults] = useState([]);

  // Email-модал
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // История
  const [filterDate, setFilterDate] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [openHistoryRows, setOpenHistoryRows] = useState({});

  const foundResults = parsedResults.filter((r) => r.student);

  const fetchHistory = useCallback(() => {
    setIsLoadingHistory(true);
    getVisaParsedSentEmails(filterDate)
      .then((res) => setHistory(res.data))
      .catch(showError)
      .finally(() => setIsLoadingHistory(false));
  }, [filterDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFilesChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setFileCount(files.length);
    setIsParsing(true);
    setParsedResults([]);

    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));

    try {
      const res = await parseVisaPdfs(formData);
      setParsedResults(res.data);
    } catch (err) {
      showError(err);
    } finally {
      setIsParsing(false);
      // Сбрасываем input чтобы можно было повторно загрузить те же файлы
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSendEmails = async ({ subject, html, useCountryTemplate }) => {
    const toUpdate = foundResults
      .filter((r) => r.stayUntil)
      .map((r) => ({ id: r.student.id, visaValidity: r.stayUntil }));

    // Собираем маппинг studentId -> visaPdfFilename и originalFileName
    const visaPdfFilenames = {};
    const originalFileNames = {};
    foundResults.forEach((r) => {
      if (r.visaPdfFilename) {
        visaPdfFilenames[r.student.id] = r.visaPdfFilename;
        originalFileNames[r.student.id] = r.originalFileName;
      }
    });

    setIsSending(true);
    try {
      if (toUpdate.length > 0) {
        const updateRes = await updateStudentsFromVisa(toUpdate);
        const { failed: updateFailed } = updateRes.data;
        if (updateFailed.length > 0) {
          iziToast.warning({
            message: `Не удалось обновить данные ${updateFailed.length} студентов`,
            position: 'topRight',
          });
        }
      }

      const studentIds = foundResults.map((r) => r.student.id);
      const res = await sendVisaEmails({ studentIds, subject, html, useCountryTemplate, visaPdfFilenames, originalFileNames });
      const { sent, failed } = res.data;
      if (failed.length > 0) {
        iziToast.warning({
          message: `Отправлено ${sent} писем. Ошибок: ${failed.length}`,
          position: 'topRight',
        });
      } else {
        iziToast.success({ message: `Отправлено ${sent} писем`, position: 'topRight' });
      }
      setEmailModalOpen(false);
      setParsedResults([]);
      fetchHistory();
    } catch (err) {
      showError(err);
    } finally {
      setIsSending(false);
    }
  };

  const toggleHistoryRow = (key) =>
    setOpenHistoryRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const modalStudents = foundResults.map((r) => ({
    id: r.student.id,
    latin_name: r.student.latin_name,
    country: r.student.country,
    stayUntil: r.stayUntil,
  }));

  return (
    <>
      {/* ── Загрузка PDF ── */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          border: '2px dashed #BDBDBD',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: '#FAFAFA',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf"
          style={{ display: 'none' }}
          onChange={handleFilesChange}
        />

        {isParsing ? (
          <>
            <CircularProgress color="warning" size={22} />
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#555' }}>
              Загружено {fileCount} {pluralDocs(fileCount)}. Выполняется парсинг…
            </Typography>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => fileInputRef.current?.click()}
              sx={{ fontFamily: 'Montserrat', textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Выбрать PDF-файлы
            </Button>
            <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', color: '#9E9E9E' }}>
              Загрузите одно или несколько визовых приглашений (электронное приглашение МВД РФ)
            </Typography>
          </>
        )}
      </Box>

      {/* ── Результаты парсинга ── */}
      {parsedResults.length > 0 && (
        <>
          <Typography
            sx={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '15px', mb: 1.5 }}
          >
            Результаты ({parsedResults.length} файлов, найдено студентов: {foundResults.length})
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                  <EmailsTableCell value="Файл" type="header" />
                  <EmailsTableCell value="Номер приглашения" type="header" />
                  <EmailsTableCell value="ФИО (лат.)" type="header" />
                  <EmailsTableCell value="Страна" type="header" />
                  <EmailsTableCell value="Срок визы" type="header" />
                  <EmailsTableCell value="Статус" type="header" />
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedResults.map((r, idx) => (
                  <TableRow key={idx}>
                    <EmailsTableCell value={r.fileName} />
                    <EmailsTableCell value={r.invitationNumber || '—'} />
                    <EmailsTableCell
                      value={
                        r.student ? (
                          <Link
                            to={studentCardPath(r.student.education_type, r.student.id)}
                            className="linkToCard"
                            target="_blank"
                          >
                            {r.student.latin_name || '—'}
                          </Link>
                        ) : (
                          <Tooltip title={r.error || ''} arrow placement="top">
                            <span style={{ color: '#C62828', cursor: 'help', fontSize: '12px' }}>
                              {r.error
                                ? r.error.length > 50
                                  ? r.error.slice(0, 50) + '…'
                                  : r.error
                                : '—'}
                            </span>
                          </Tooltip>
                        )
                      }
                    />
                    <EmailsTableCell value={
                      r.student ? (
                        <Link to={studentCardPath(r.student.education_type, r.student.id)} className="linkToCard" target="_blank">
                          {r.student.country || '—'}
                        </Link>
                      ) : (
                        r.student?.country || '—'
                      )
                    } />
                    <EmailsTableCell value={r.stayUntil || '—'} />
                    <ResultStatusCell result={r} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Кнопки действий ── */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              color="warning"
              onClick={() => setEmailModalOpen(true)}
              disabled={foundResults.length === 0}
              sx={{ fontFamily: 'Montserrat', textTransform: 'none' }}
            >
              {`Отправить рассылку (${foundResults.length} студ.)`}
            </Button>
          </Box>
        </>
      )}

      {/* ── История рассылок ── */}
      <Typography
        sx={{
          fontFamily: 'Montserrat',
          fontWeight: 600,
          fontSize: '15px',
          mb: 1.5,
          mt: parsedResults.length > 0 ? 1 : 0,
        }}
      >
        История рассылок
      </Typography>

      <FilterRow isLoading={isLoadingHistory}>
        <CustomSingleDatePicker
          name="sent_at"
          label="Фильтр по дате рассылки"
          size="default"
          action={setFilterDate}
        />
      </FilterRow>

      <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <EmailsTableCell value="Дата отправки" type="header" />
              <EmailsTableCell value="Кол-во студентов" type="header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length > 0 ? (
              history.map((row) => (
                <Fragment key={row.send_date}>
                  <TableRow>
                    <TableCell sx={{ width: 40, p: '4px 8px' }}>
                      <IconButton size="small" onClick={() => toggleHistoryRow(row.send_date)}>
                        {openHistoryRows[row.send_date] ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <EmailsTableCell value={moment(row.sent_at).format('DD.MM.YYYY HH:mm')} />
                    <EmailsTableCell value={row.students_count} />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0 }}>
                      <Collapse in={!!openHistoryRows[row.send_date]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <EmailsTableCell value="ФИО (лат.)" />
                                <EmailsTableCell value="ФИО (кир.)" />
                                <EmailsTableCell value="Страна" />
                                <EmailsTableCell value="Срок визы" />
                                <EmailsTableCell value="Ссылка на письмо" />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.students.map((s) => (
                                <TableRow key={s.id}>
                                  <EmailsTableCell
                                    value={
                                      <Link
                                        to={studentCardPath(s.education_type, s.id)}
                                        className="linkToCard"
                                      >
                                        {s.latin_name || '—'}
                                      </Link>
                                    }
                                  />
                                  <EmailsTableCell
                                    value={
                                      <Link
                                        to={studentCardPath(s.education_type, s.id)}
                                        className="linkToCard"
                                      >
                                        {s.russian_name || '—'}
                                      </Link>
                                    }
                                  />
                                  <EmailsTableCell value={s.country || '—'} />
                                  <EmailsTableCell
                                    value={
                                      s.visa_validity
                                        ? moment(s.visa_validity).format('DD.MM.YYYY')
                                        : '—'
                                    }
                                  />
                                  <EmailsTableCell value={<EmailLink emailId={s.email_id} />} />
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))
            ) : (
              <NoDataRow colSpan={4} />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Модал рассылки ── */}
      <ModalCustom active={emailModalOpen} setActive={setEmailModalOpen}>
        <VisaEmailModal
          students={modalStudents}
          onSend={handleSendEmails}
          onClose={() => setEmailModalOpen(false)}
          isSending={isSending}
        />
      </ModalCustom>
    </>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const TAB_LABELS = [
  'Нахождение в РФ (25 дней)',
  'Визовые приглашения (55 дней)',
  'Согласование проректора',
  'Отчисленные',
  'Ручная рассылка',
  'Визовые приглашения (PDF)',
];

export const EmailsPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Header />
      <div className="emailsBlock">
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #BDBDBD',
            '& .MuiTabs-indicator': { backgroundColor: '#FA7A45' },
            '& .MuiTab-root': {
              fontFamily: 'Montserrat',
              textTransform: 'none',
              fontSize: '14px',
            },
            '& .Mui-selected': { color: '#FA7A45 !important', fontWeight: 600 },
          }}
        >
          {TAB_LABELS.map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <RfLocation25Tab />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <VisaInvitation55Tab />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <RectorApprovalTab />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <SimpleEmailsTab fetchFn={getStudentExpelledSentEmails} />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <SimpleEmailsTab fetchFn={getManualSentEmails} />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <VisaParsingTab />
        </TabPanel>
      </div>
    </>
  );
};
